import React from 'react';
import type { ChartDataPoint } from '../types';

interface HorizontalBarChartProps {
  data: ChartDataPoint[];
  title?: string;
  onBarClick?: (dataPoint: ChartDataPoint) => void;
}

const BAR_COLORS = [
  '#f59e0b', // amber-500
  '#fbbf24', // amber-400
  '#fcd34d', // amber-300
  '#d97706', // amber-600
  '#b45309', // amber-700
  '#92400e', // amber-800
];

/**
 * Renders a horizontal bar graph of category counts.
 * Uses pure CSS/HTML instead of Recharts to reduce dependencies.
 */
const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({
  data,
  title = 'Results Summary',
  onBarClick,
}) => {
  // Guard against undefined or empty data
  if (!data || data.length === 0) {
    return null;
  }

  const maxValue = Math.max(...data.map((d) => d.value), 1);

  return (
    <div className="ves-chart-container">
      <div className="ves-chart-title">{title}</div>
      <div className="ves-chart-bars">
        {data.map((item, index) => {
          const handleKeyDown = (event: React.KeyboardEvent) => {
            if (!onBarClick) return;
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              onBarClick(item);
            }
          };

          return (
            <div
              key={item.label}
              className="ves-chart-row"
              role={onBarClick ? 'button' : undefined}
              tabIndex={onBarClick ? 0 : undefined}
              onClick={() => onBarClick?.(item)}
              onKeyDown={handleKeyDown}
              style={{ cursor: onBarClick ? 'pointer' : 'default' }}
            >
              <div className="ves-chart-label">{item.label}</div>
              <div className="ves-chart-bar-container">
                <div
                  className="ves-chart-bar"
                  style={{
                    width: `${(item.value / maxValue) * 100}%`,
                    backgroundColor: BAR_COLORS[index % BAR_COLORS.length],
                  }}
                />
                <span className="ves-chart-value">{item.value}</span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HorizontalBarChart;
